'use client';
import React, {
	forwardRef,
	useCallback,
	useImperativeHandle,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';
import {
	isValid,
	startOfDay,
	endOfDay,
	isSameDay,
	isBefore,
	isEqual,
	setDay,
	format,
	getDay,
	startOfWeek,
} from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import Navigation from './Navigation';
import MonthView from './MonthView';
import { getBegin, getBeginNext, getEnd, getValueRange } from './shared/dates';
import { between } from './shared/utils';
import type {
	ClassName,
	NavigationLabelFunc,
	OnClickFunc,
	OnClickWeekNumberFunc,
	TileClassNameFunc,
	TileContentFunc,
	TileDisabledFunc,
} from './shared/types';
import type { Action, LooseValue, Oj, OnArgs, Range, Value } from '@fshr/shared/types';
import type {
	formatDay as defaultFormatDay,
	formatLongDate as defaultFormatLongDate,
	formatShortWeekday as defaultFormatShortWeekday,
	formatWeekday as defaultFormatWeekday,
} from './shared/dateFormatter';
import { formatMonthYear as defaultFormatMonthYear } from './shared/dateFormatter';
import { cn } from '@fshr/shared-react';
import { useTranslations } from '@fshr/context/i18n-context';
import { CalendarFooter } from './footer';
import { IconCloseAlt } from '../icon';
import HorizontalLine from '../horizontal-line';

export type CalendarStatus = '0' | '9' | '.' | 'R' | '?';

export type CalendarProps = {
	activeStartDate?: Date;
	allowPartialRange?: boolean;
	className?: ClassName;
	defaultActiveStartDate?: Date;
	defaultValue?: LooseValue;
	formatDay?: typeof defaultFormatDay;
	formatLongDate?: typeof defaultFormatLongDate;
	formatMonthYear?: typeof defaultFormatMonthYear;
	formatShortWeekday?: typeof defaultFormatShortWeekday;
	formatWeekday?: typeof defaultFormatWeekday;
	goToRangeStartOnSelect?: boolean;
	inputRef?: React.Ref<HTMLDivElement>;
	locale: string;
	maxDate?: Date;
	minDate?: Date;
	navigationAriaLabel?: string | ((activeStartDate: Date, showDoubleView?: boolean) => string);
	navigationAriaLive?: 'off' | 'polite' | 'assertive';
	navigationLabel?: NavigationLabelFunc;
	next2AriaLabel?: string;
	next2Label?: React.ReactNode;
	nextAriaLabel?: string;
	nextLabel?: React.ReactNode;
	onActiveStartDateChange?: ({ action, activeStartDate, value }: OnArgs) => void;
	onChange?: (value: Value, event: React.MouseEvent<HTMLButtonElement>) => void;
	onClickDay?: OnClickFunc;
	onClickWeekNumber?: OnClickWeekNumberFunc;
	onClose?: () => void;
	prev2AriaLabel?: string;
	prev2Label?: React.ReactNode;
	prevAriaLabel?: string;
	returnValue?: 'start' | 'end' | 'range';
	selectRange?: boolean;
	strictRangeSelect?: boolean;
	showDoubleView?: boolean;
	showFixedNumberOfWeeks?: boolean;
	showNavigation?: boolean;
	showNeighboringMonth?: boolean;
	showWeekNumbers?: boolean;
	tileClassName?: TileClassNameFunc | ClassName;
	tileContent?: TileContentFunc | React.ReactNode;
	tileDisabled?: TileDisabledFunc;
	value?: LooseValue;
	valueLabel?: string;
	timezone?: string;
	tileAriaLabel?: (d: Date) => string;
	unavailableDates: Date[];
	restrictedDates: Date[];
	/** When true, unavailable dates stay styled as sold out but are not disabled by that status alone. */
	allowUnavailableSelection?: boolean;
	isCheckoutDateRequired?: boolean;
	onSubmit?: () => void;
	onFooterButtonBlur?: () => void;
	onFooterButtonKeyDown?: (e: any) => void;
	isMobile?: boolean;
	baseClassName?: string;
	showFooter?: boolean;
	showMobileHeader?: boolean;
	monthClassName?: string;
	lastUpdated?: string;
	showLegend?: boolean;
	availabilityData?: Oj.CalendarAvailability[];
	nightlyRatesExperiment?: boolean;
};

function toDate(value: Date | string, timezone: string): Date {
	if (typeof value === 'string') {
		return toZonedTime(value, timezone);
	}

	return value;
}

function getValue(value: LooseValue | undefined, index: 0 | 1, timezone: string): Date | null {
	const rawValue = Array.isArray(value) ? value[index] : value;

	if (!rawValue) {
		return null;
	}

	const valueDate = toDate(rawValue, timezone);

	if (!isValid(valueDate)) {
		throw new Error(`Invalid date: ${value}`);
	}

	return valueDate;
}

type DetailArgs = {
	value?: LooseValue;
	minDate?: Date;
	maxDate?: Date;
	activeStartDate?: Date;
	timezone?: string;
	strictRangeSelect?: boolean;
};

function getDetailValue({ value, minDate, maxDate, timezone }: DetailArgs, index: 0 | 1) {
	const valuePiece = getValue(value, index, timezone!);

	if (!valuePiece) {
		return null;
	}

	const detailValueFrom = (() => {
		switch (index) {
			case 0:
				return getBegin(valuePiece);
			case 1:
				return getEnd(valuePiece);
			default:
				throw new Error(`Invalid index value: ${index}`);
		}
	})();

	return between(detailValueFrom, minDate, maxDate);
}

const getDetailValueFrom = (args: DetailArgs) => getDetailValue(args, 0);

const getDetailValueTo = (args: DetailArgs) => getDetailValue(args, 1);

const getDetailValueArray = (args: DetailArgs) => {
	const { value, strictRangeSelect } = args;
	if (strictRangeSelect && Array.isArray(value)) {
		const [start, end] = value;
		if (
			isSameDay(
				startOfDay(toZonedTime(end!, args.timezone!)),
				typeof start === 'string' ? new Date(start) : start!
			)
		) {
			args.value = [end, null];
		}
	}
	return [getDetailValueFrom, getDetailValueTo].map(fn => fn(args)) as [
		ReturnType<typeof getDetailValueFrom>,
		ReturnType<typeof getDetailValueTo>,
	];
};
function getActiveStartDate({ maxDate, minDate, value, timezone, activeStartDate }: DetailArgs) {
	const valueFrom =
		getDetailValueFrom({
			value,
			minDate,
			maxDate,
			timezone,
		}) || toZonedTime(new Date(), timezone!);

	if (activeStartDate && activeStartDate.getMonth() === valueFrom.getMonth() - 1) {
		return getBegin(activeStartDate);
	}

	return getBegin(valueFrom);
}

function getInitialActiveStartDate({
	activeStartDate,
	defaultActiveStartDate,
	defaultValue,
	maxDate,
	minDate,
	value,
	timezone,
}: {
	activeStartDate?: Date;
	defaultActiveStartDate?: Date;
	defaultValue?: LooseValue;
	maxDate: Date;
	minDate: Date;
	value?: LooseValue;
	timezone?: string;
}) {
	const valueFrom = activeStartDate || defaultActiveStartDate;

	if (valueFrom) {
		return getBegin(valueFrom);
	}

	return getActiveStartDate({
		activeStartDate,
		maxDate,
		minDate,
		value: value || defaultValue,
		timezone,
	});
}

function getIsSingleValue<T>(value: T | T[]): value is T {
	return value && (!Array.isArray(value) || value.length === 1);
}

function areDatesEqual(date1?: Date | null, date2?: Date | null) {
	return date1 instanceof Date && date2 instanceof Date && date1.getTime() === date2.getTime();
}

const Calendar = forwardRef(function Calendar(props: CalendarProps, ref) {
	const t = useTranslations();
	const defaultMinDate = startOfDay(toZonedTime(new Date(), props.timezone!));
	const defaultMaxDate = endOfDay(toZonedTime(new Date(8.64e15 - 1), props.timezone!));

	const {
		activeStartDate: activeStartDateProps,
		allowPartialRange,
		className,
		defaultActiveStartDate,
		defaultValue,
		formatDay,
		formatLongDate,
		formatMonthYear,
		formatShortWeekday,
		formatWeekday,
		goToRangeStartOnSelect = true,
		inputRef,
		locale,
		maxDate = defaultMaxDate,
		minDate = defaultMinDate,
		navigationAriaLabel,
		navigationAriaLive,
		navigationLabel,
		next2AriaLabel,
		next2Label,
		nextAriaLabel,
		onActiveStartDateChange,
		onChange: onChangeProps,
		onClickDay,
		onClickWeekNumber,
		onClose,
		prev2AriaLabel,
		prev2Label,
		prevAriaLabel,
		returnValue = 'start',
		selectRange,
		showDoubleView,
		showFixedNumberOfWeeks,
		showNavigation = true,
		showNeighboringMonth = true,
		showWeekNumbers,
		tileAriaLabel,
		tileClassName,
		tileContent,
		tileDisabled,
		value: valueProps,
		timezone,
		strictRangeSelect,
		unavailableDates,
		restrictedDates,
		allowUnavailableSelection = false,
		valueLabel,
		isCheckoutDateRequired,
		onSubmit,
		onFooterButtonBlur,
		onFooterButtonKeyDown,
		isMobile,
		baseClassName,
		showFooter = true,
		showMobileHeader = true,
		lastUpdated,
		showLegend = true,
		availabilityData,
		nightlyRatesExperiment,
		monthClassName,
	} = props;

	const [activeStartDateState, setActiveStartDateState] = useState<Date | null | undefined>(
		defaultActiveStartDate
	);

	const [valueState, setValueState] = useState<Value | undefined>(
		Array.isArray(defaultValue)
			? (defaultValue.map(el => (el !== null ? toDate(el, timezone!) : null)) as Range<Date | null>)
			: defaultValue !== null && defaultValue !== undefined
				? toDate(defaultValue, timezone!)
				: null
	);

	const activeStartDate =
		activeStartDateProps ||
		activeStartDateState ||
		getInitialActiveStartDate({
			activeStartDate: activeStartDateProps,
			defaultActiveStartDate,
			defaultValue,
			maxDate,
			minDate,
			value: valueProps,
			timezone,
		});

	const value: Value = (() => {
		const rawValue = (() => {
			// In the middle of range selection, use value from state
			if (selectRange && getIsSingleValue(valueState)) {
				return valueState;
			}
			return valueProps !== undefined ? valueProps : valueState;
		})();

		if (!rawValue) {
			return null;
		}

		return Array.isArray(rawValue)
			? (rawValue.map(el => (el !== null ? toDate(el, timezone!) : null)) as Range<Date | null>)
			: rawValue !== null
				? toDate(rawValue, timezone!)
				: null;
	})();

	const getProcessedValue = useCallback(
		(value: Date) => {
			const processFunction = (() => {
				switch (returnValue) {
					case 'start':
						return getDetailValueFrom;
					case 'end':
						return getDetailValueTo;
					case 'range':
						return getDetailValueArray;
					default:
						throw new Error('Invalid returnValue.');
				}
			})();

			return processFunction({
				maxDate,
				minDate,
				value,
				timezone,
			});
		},
		[maxDate, minDate, returnValue, timezone]
	);

	const setActiveStartDate = useCallback(
		(nextActiveStartDate: Date, action: Action) => {
			setActiveStartDateState(nextActiveStartDate);

			const args: OnArgs = {
				action,
				activeStartDate: nextActiveStartDate,
				value,
			};

			if (onActiveStartDateChange && !areDatesEqual(activeStartDate, nextActiveStartDate)) {
				onActiveStartDateChange(args);
			}
		},
		[activeStartDate, onActiveStartDateChange, value]
	);

	const onClickTile = useCallback(
		(value: Date, event: React.MouseEvent<HTMLButtonElement>) => {
			const callback = (() => {
				return onClickDay;
			})();

			if (callback) callback(value, event);
		},
		[onClickDay]
	);

	const onChange = useCallback(
		(rawNextValue: Date, event: React.MouseEvent<HTMLButtonElement>) => {
			const previousValue = value;

			onClickTile(rawNextValue, event);

			const isFirstValueInRange = selectRange && !getIsSingleValue(previousValue);

			let nextValue: Value;
			if (selectRange) {
				// Range selection turned on

				if (isFirstValueInRange) {
					// Value has 0 or 2 elements - either way we're starting a new array
					// First value
					nextValue = getBegin(rawNextValue);
				} else {
					if (!previousValue) {
						throw new Error('previousValue is required');
					}

					if (Array.isArray(previousValue)) {
						throw new Error('previousValue must not be an array');
					}

					const nextValueZoned = toZonedTime(rawNextValue, timezone!);
					// In strict range select, selecting a second date in the past results in a new first date.
					if (
						(strictRangeSelect && isEqual(nextValueZoned, previousValue)) ||
						isBefore(nextValueZoned, previousValue)
					) {
						nextValue = getBegin(rawNextValue);
					} else {
						// Second value
						nextValue = getValueRange(previousValue, rawNextValue);
					}
				}
			} else {
				// Range selection turned off
				nextValue = getProcessedValue(rawNextValue);
			}

			const nextActiveStartDate =
				// Range selection turned off
				!selectRange ||
				// Range selection turned on, first value
				isFirstValueInRange ||
				// Range selection turned on, second value, goToRangeStartOnSelect toggled on
				goToRangeStartOnSelect ||
				// Range select reseulted in a reset
				(strictRangeSelect && !Array.isArray(nextValue))
					? getActiveStartDate({
							activeStartDate,
							maxDate,
							minDate,
							value: nextValue,
							timezone,
						})
					: null;

			event.persist();

			if (
				showDoubleView &&
				nextActiveStartDate &&
				activeStartDate &&
				activeStartDate.getMonth() !== nextActiveStartDate?.getMonth() - 1
			) {
				setActiveStartDateState(nextActiveStartDate);
			}
			setValueState(nextValue);

			const args: OnArgs = {
				action: 'onChange',
				activeStartDate: nextActiveStartDate,
				value: nextValue,
			};

			if (onActiveStartDateChange && !areDatesEqual(activeStartDate, nextActiveStartDate)) {
				onActiveStartDateChange(args);
			}

			if (onChangeProps) {
				if (selectRange) {
					const isSingleValue = getIsSingleValue(nextValue);

					if (!isSingleValue) {
						onChangeProps(nextValue || null, event);
					} else if (allowPartialRange) {
						if (Array.isArray(nextValue)) {
							throw new Error('value must not be an array');
						}

						onChangeProps([nextValue || null, null], event);
					}
				} else {
					onChangeProps(nextValue || null, event);
				}
			}
		},
		[
			activeStartDate,
			allowPartialRange,
			getProcessedValue,
			goToRangeStartOnSelect,
			maxDate,
			minDate,
			onActiveStartDateChange,
			onChangeProps,
			onClickTile,
			selectRange,
			value,
			timezone,
			showDoubleView,
			strictRangeSelect,
		]
	);

	useImperativeHandle(
		ref,
		() => ({
			activeStartDate,
			onChange,
			setActiveStartDate,
			value,
		}),
		[activeStartDate, onChange, setActiveStartDate, value]
	);

	const monthHeaderRef = useRef<HTMLSpanElement>(null);
	useLayoutEffect(() => {
		if (monthHeaderRef.current) {
			monthHeaderRef.current.scrollIntoView({ behavior: 'instant' });
		}
	}, [monthHeaderRef]);

	function renderContent(next?: boolean) {
		const currentActiveStartDate = next ? getBeginNext(activeStartDate) : getBegin(activeStartDate);

		const onClick = onChange;

		const commonProps = {
			activeStartDate: currentActiveStartDate,
			locale,
			maxDate,
			minDate,
			onClick,
			tileAriaLabel,
			tileClassName,
			tileContent,
			tileDisabled,
			value,
			availabilityData,
			nightlyRatesExperiment,
			allowUnavailableSelection,
		};

		if (props.isMobile) {
			const firstDayOfCurrentMonth = new Date(new Date().setDate(1));
			return (
				<div
					className={cn('flex flex-col w-full spacing-stack-m', {
						'!pb-[135px]': showFooter,
					})}
				>
					{Array.from({ length: 13 }).map((_, index) => {
						const _today = new Date(firstDayOfCurrentMonth);
						const calendarActiveStartDate = new Date(
							_today.setMonth(_today.getMonth() + index)
						).setDate(1);

						const selectedDate = value?.[0] ? new Date(value[0]) : null;
						const activeDate = new Date(calendarActiveStartDate);
						const isSelectedMonth = selectedDate
							? selectedDate.getMonth() === activeDate.getMonth() &&
								selectedDate.getFullYear() === activeDate.getFullYear()
							: false;
						return (
							<div
								key={`calendar-month-${index}`}
								className={cn('flex flex-col spacing-stack-m text-label', monthClassName)}
							>
								<span
									ref={instance => {
										if (isSelectedMonth) monthHeaderRef.current = instance;
									}}
									className={cn(
										`${baseClassName}__month-header${isSelectedMonth ? '--selected' : ''} w-full flex flex-row items-center justify-start font-normal text-text-heading text-[12px]`,
										{
											'scroll-m-[37px]': !showMobileHeader,
											'scroll-m-[105px]': showMobileHeader,
										}
									)}
								>
									{(formatMonthYear || defaultFormatMonthYear)(
										locale,
										new Date(calendarActiveStartDate)
									)}
								</span>
								<MonthView
									restrictedDates={restrictedDates}
									unavailableDates={unavailableDates}
									formatDay={formatDay}
									formatLongDate={formatLongDate}
									formatShortWeekday={formatShortWeekday}
									formatWeekday={formatWeekday}
									onClickWeekNumber={onClickWeekNumber}
									showFixedNumberOfWeeks={
										typeof showFixedNumberOfWeeks !== 'undefined'
											? showFixedNumberOfWeeks
											: showDoubleView
									}
									showNeighboringMonth={showNeighboringMonth}
									showWeekNumbers={showWeekNumbers}
									isMobile={isMobile}
									{...commonProps}
									activeStartDate={new Date(calendarActiveStartDate)}
									baseClassName={baseClassName}
								/>
							</div>
						);
					})}
				</div>
			);
		}

		return (
			<MonthView
				restrictedDates={restrictedDates}
				unavailableDates={unavailableDates}
				formatDay={formatDay}
				formatLongDate={formatLongDate}
				formatShortWeekday={formatShortWeekday}
				formatWeekday={formatWeekday}
				onClickWeekNumber={onClickWeekNumber}
				showFixedNumberOfWeeks={
					typeof showFixedNumberOfWeeks !== 'undefined' ? showFixedNumberOfWeeks : showDoubleView
				}
				showNeighboringMonth={showNeighboringMonth}
				showWeekNumbers={showWeekNumbers}
				isMobile={isMobile}
				baseClassName={baseClassName}
				{...commonProps}
			/>
		);
	}

	function renderNavigation() {
		if (!showNavigation) {
			return null;
		}

		return (
			<Navigation
				activeStartDate={activeStartDate}
				formatMonthYear={formatMonthYear}
				locale={locale}
				maxDate={maxDate}
				minDate={minDate}
				navigationAriaLabel={navigationAriaLabel}
				navigationAriaLive={navigationAriaLive}
				navigationLabel={navigationLabel}
				next2AriaLabel={next2AriaLabel}
				next2Label={next2Label}
				nextAriaLabel={nextAriaLabel}
				prev2AriaLabel={prev2AriaLabel}
				prev2Label={prev2Label}
				prevAriaLabel={prevAriaLabel}
				setActiveStartDate={setActiveStartDate}
				showDoubleView={showDoubleView}
			/>
		);
	}

	function renderMobileHeader() {
		if (!isMobile) {
			return null;
		}
		const firstDayOfWeek = getDay(startOfWeek(new Date(), { weekStartsOn: 0 }));

		return (
			<div
				className={cn('MobileCAWDialog-section WeekHeader MobileCAWDialog-transition z-100', {
					'bg-white fixed top-0 left-0 right-0': showMobileHeader,
					'absolute w-[100vw] -left-[15px] -top-[15px] bg-surface': !showMobileHeader,
				})}
			>
				{showMobileHeader && (
					<>
						<div className="flex items-center justify-between px-[20px] py-[15px] relative">
							<h2 className="text-subtitle2 font-bold flex-1 text-center">{t('Select Dates')}</h2>
							<button
								onClick={() => {
									onClose?.();
								}}
								className="p-[8px] rounded-full hover:bg-[#F5F5F5] transition-colors absolute right-[20px] flex items-center justify-center"
								aria-label={t('Close')}
							>
								<IconCloseAlt width={20} height={20} fill="#000000" />
							</button>
						</div>

						<HorizontalLine additionalClasses="border-border-subtle" />
					</>
				)}

				<div className="spacing-card-padding-x-xs flex justify-center items-start text-black text-center text-body3">
					{[0, 1, 2, 3, 4, 5, 6]
						.map(i => setDay(new Date(), (i + firstDayOfWeek) % 7))
						.map(date => (
							<div
								aria-hidden="true"
								key={format(date, 'EEEEEE')}
								className="DayPicker_container max-h-[37px] h-[36px] flex items-center justify-center w-full text-label basis-1/7"
							>
								<span className="text-text-secondary text-[12px] -mr-[1.8px]">
									{format(date, 'EEEEE')}
								</span>
							</div>
						))}
				</div>
				<HorizontalLine additionalClasses="border-border-subtle" />
			</div>
		);
	}

	const valueArray = Array.isArray(value) ? value : [value];

	function renderFooter() {
		if (!showFooter) {
			return null;
		}
		return (
			<CalendarFooter
				isCheckoutDateRequired={isCheckoutDateRequired}
				onSubmit={onSubmit}
				onBlur={onFooterButtonBlur}
				onKeyDown={onFooterButtonKeyDown}
				valueLabel={valueLabel}
				lastUpdated={lastUpdated}
				isMobile={props.isMobile}
				showLegend={showLegend}
				buttonDisabled={valueArray.length <= 1}
			/>
		);
	}

	return (
		<div className={cn('flex-col w-full')}>
			{renderMobileHeader()}

			<div
				className={cn(
					baseClassName,
					'flex flex-col bg-surface spacing-stack-m ',
					{
						'pt-[90px]': isMobile && showMobileHeader,
						'spacing-card-padding-xs': !isMobile,
					},
					selectRange && valueArray.length === 1 && `${baseClassName}--selectRange`,
					showDoubleView && `${baseClassName}--doubleView`,
					className
				)}
				ref={inputRef}
				aria-label={t('Stay Dates')}
			>
				{renderNavigation()}
				<div
					className={cn(`${baseClassName}__viewContainer flex`, {
						'flex-row spacing-inline-l justify-between': !isMobile,
						'flex-col': isMobile,
					})}
				>
					{renderContent()}
					{showDoubleView ? renderContent(true) : null}
				</div>
			</div>
			{renderFooter()}
		</div>
	);
});

export default Calendar;
